import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDeleteApiWorkspacesWorkspaceId, useGetApiWorkspaces } from '@/api/generated/workspaces/workspaces';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

interface DeleteWorkspaceModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  workspaceName: string;
}

export const DeleteWorkspaceModal: React.FC<DeleteWorkspaceModalProps> = ({
  open,
  onOpenChange,
  workspaceId,
  workspaceName,
}) => {
  const [confirmName, setConfirmName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const deleteMutation = useDeleteApiWorkspacesWorkspaceId();
  const { setWorkspaces } = useWorkspaceStore();
  const { refetch } = useGetApiWorkspaces();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (open) {
      setConfirmName('');
      setIsSubmitting(false);
    }
  }, [open]);

  const isConfirmed = confirmName.trim() === workspaceName.trim();

  const handleDelete = async () => {
    if (!isConfirmed) {
      toast.error('O nome digitado não confere.');
      return;
    }

    setIsSubmitting(true);
    try {
      await deleteMutation.mutateAsync({
        workspaceId,
        params: { confirmName: confirmName.trim() },
        data: { confirmWorkspaceName: confirmName.trim() },
      });

      toast.success('Workspace excluído com sucesso.');

      const { data: workspacesList } = await refetch();
      if (workspacesList) {
        setWorkspaces(workspacesList as any[]);
      }

      onOpenChange(false);
      navigate('/');
    } catch {
      // Global interceptor handles CannotDeleteLastWorkspace / ActiveSubscriptionPreventsDeletion / NameConfirmationMismatch
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader>
          <div className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            <DialogTitle>Excluir Workspace</DialogTitle>
          </div>
          <DialogDescription className="pt-2 text-left space-y-2">
            <span>
              Esta ação excluirá permanentemente o workspace <strong>{workspaceName}</strong>, incluindo lojas associadas, membros e histórico.
            </span>
            <span className="block text-xs font-medium text-destructive">
              Para confirmar, digite <strong>{workspaceName}</strong> abaixo:
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="confirm-workspace-name">Nome do Workspace</Label>
            <Input
              id="confirm-workspace-name"
              value={confirmName}
              onChange={(e) => setConfirmName(e.target.value)}
              placeholder={workspaceName}
              autoComplete="off"
            />
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleDelete}
            disabled={!isConfirmed || isSubmitting}
          >
            {isSubmitting ? 'Excluindo...' : 'Tenho certeza, excluir workspace'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
